import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../../config/api';
import { Input, Select, Textarea } from '../UI';
import { unwrap } from './dnaTypes';

/** Angaben zu einem nicht erfassten Hund - im Import-Assistenten und beim Bearbeiten einer Probe. */
export interface SampleDetails {
  name: string;
  sex: 'Female' | 'Male' | 'Unknown';
  breedId: string;
  dateOfBirth: string;
  microchipNumber: string;
  holderName: string;
  notes: string;
}

export const emptySampleDetails: SampleDetails = {
  name: '',
  sex: 'Unknown',
  breedId: '',
  dateOfBirth: '',
  microchipNumber: '',
  holderName: '',
  notes: ''
};

interface Breed {
  id: number;
  nameGerman: string;
  nameEnglish: string;
}

interface SampleDetailsFormProps {
  value: SampleDetails;
  onChange: (value: SampleDetails) => void;
  /** Zeigt zu jedem Feld einen kurzen Hinweis - im Assistenten an, beim Bearbeiten aus. */
  withHints?: boolean;
  nameError?: string;
}

export const SampleDetailsForm: React.FC<SampleDetailsFormProps> = ({ value, onChange, withHints = false, nameError }) => {
  const { t } = useTranslation();
  const [breeds, setBreeds] = useState<Breed[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchApi('/breeds')
      .then((res) => {
        const data = unwrap<Breed[]>(res);
        if (!cancelled && Array.isArray(data)) setBreeds(data);
      })
      .catch(() => {
        // Ohne Rassenliste bleibt das Feld leer - die Angabe ist freiwillig.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = <K extends keyof SampleDetails>(key: K, next: SampleDetails[K]) => onChange({ ...value, [key]: next });
  const hint = (key: string) => withHints && <p className="mt-1 text-xs text-dark-400">{t(`dna:sampleForm.hints.${key}`)}</p>;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Input
          label={`${t('dna:sampleForm.name')} *`}
          value={value.name}
          maxLength={200}
          error={nameError}
          onChange={(e) => set('name', e.target.value)}
        />
        {hint('name')}
      </div>
      <div>
        <Select
          label={t('dna:sampleForm.sex')}
          value={value.sex}
          onChange={(e) => set('sex', e.target.value as SampleDetails['sex'])}
          options={[
            { value: 'Unknown', label: t('dogs:sex.unknown') },
            { value: 'Female', label: t('dogs:sex.female') },
            { value: 'Male', label: t('dogs:sex.male') }
          ]}
        />
        {hint('sex')}
      </div>
      <div>
        <Select
          label={t('dna:sampleForm.breed')}
          value={value.breedId}
          onChange={(e) => set('breedId', e.target.value)}
          options={[
            { value: '', label: t('dna:sampleForm.breedNone') },
            ...breeds.map((breed) => ({ value: breed.id, label: `${breed.nameGerman} (${breed.nameEnglish})` }))
          ]}
        />
        {hint('breed')}
      </div>
      <div>
        <Input label={t('dna:sampleForm.dateOfBirth')} type="date" value={value.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
      </div>
      <div>
        <Input label={t('dna:sampleForm.microchip')} value={value.microchipNumber} maxLength={50} onChange={(e) => set('microchipNumber', e.target.value)} />
        {hint('microchip')}
      </div>
      <div className="sm:col-span-2">
        <Input label={t('dna:sampleForm.holder')} value={value.holderName} maxLength={200} onChange={(e) => set('holderName', e.target.value)} />
        {hint('holder')}
      </div>
      <div className="sm:col-span-2">
        <Textarea label={t('dna:sampleForm.notes')} value={value.notes} maxLength={2000} rows={3} onChange={(e) => set('notes', e.target.value)} />
      </div>
    </div>
  );
};

/** Der Server bekommt Datum und Rasse als echte Werte, leere Felder als fehlend. */
export const sampleDetailsToPayload = (details: SampleDetails) => ({
  name: details.name.trim(),
  sex: details.sex,
  breedId: details.breedId ? Number(details.breedId) : null,
  dateOfBirth: details.dateOfBirth || null,
  microchipNumber: details.microchipNumber.trim() || null,
  holderName: details.holderName.trim() || null,
  notes: details.notes.trim() || null
});
